import { Command, CommandInput, CommandList, CommandEmpty } from "@components/ui/command";
import { CommandLoading } from "cmdk";
import { type KeyboardEvent, useCallback, useRef, useState } from "react";
import { cn } from "@components/utils/utils";
import { Spinner } from "@shared-kernel/primary/shared/ui/spinner";
import { type AdminGlobalSearchVM, SEARCH_ENTITY_TYPE } from "@shared-kernel/application/ports/admin/admin-repository";
import { StudentResultGroup } from "@user-management-context/primary/shared/admin-global-search/result-groups/student-result-group";
import { TeacherResultGroup } from "@user-management-context/primary/shared/admin-global-search/result-groups/teacher-result-group";
import { ProviderResultGroup } from "@user-management-context/primary/shared/admin-global-search/result-groups/provider-result-group";

interface Props {
  query: string;
  onQueryChange: (value: string) => void;
  results: AdminGlobalSearchVM | null;
  isLoading: boolean;
  onSelectResult: (entityType: SEARCH_ENTITY_TYPE, entityId: string) => void;
  getEntityUrl: (entityType: SEARCH_ENTITY_TYPE, entityId: string) => string;
}

export const AdminGlobalSearchContent = ({ query, onQueryChange, results, isLoading, onSelectResult, getEntityUrl }: Props) => {
  const [isOpen, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!inputRef.current) return;
      if (!isOpen) setOpen(true);
      if (event.key === "Escape") inputRef.current.blur();
    },
    [isOpen],
  );

  const handleBlur = useCallback(() => {
    setOpen(false);
  }, []);

  const handlePreventBlur = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleClose = useCallback(() => {
    inputRef.current?.blur();
  }, []);

  const handleSelect = useCallback(
    (entityType: SEARCH_ENTITY_TYPE, entityId: string) => {
      onSelectResult(entityType, entityId);
      handleClose();
    },
    [onSelectResult, handleClose],
  );

  const showResults = isOpen && query.length >= 2;

  return (
    <Command onKeyDown={handleKeyDown} shouldFilter={false} className="overflow-visible bg-transparent">
      <CommandInput
        ref={inputRef}
        value={query}
        onValueChange={onQueryChange}
        onBlur={handleBlur}
        onFocus={() => setOpen(true)}
        placeholder="Rechercher ..."
        className="md:min-w-[450px]"
        containerClassName="rounded-full"
      />
      <div className="relative mt-1">
        <div
          className={cn(
            "animate-in fade-in-0 zoom-in-95 absolute top-0 z-10 w-full rounded-xl bg-popover outline-none",
            showResults ? "block" : "hidden",
          )}
          onMouseDown={handlePreventBlur}
        >
          <CommandList className="rounded-lg ring-1 ring-border">
            {isLoading ? (
              <CommandLoading>
                <div className="flex items-center justify-center p-4">
                  <Spinner size="sm" />
                </div>
              </CommandLoading>
            ) : results ? (
              <>
                <StudentResultGroup
                  students={results[SEARCH_ENTITY_TYPE.STUDENT]}
                  onSelect={entityId => handleSelect(SEARCH_ENTITY_TYPE.STUDENT, entityId)}
                  getUrl={entityId => getEntityUrl(SEARCH_ENTITY_TYPE.STUDENT, entityId)}
                  onClose={handleClose}
                />
                <TeacherResultGroup
                  teachers={results[SEARCH_ENTITY_TYPE.TEACHER]}
                  onSelect={entityId => handleSelect(SEARCH_ENTITY_TYPE.TEACHER, entityId)}
                  getUrl={entityId => getEntityUrl(SEARCH_ENTITY_TYPE.TEACHER, entityId)}
                  onClose={handleClose}
                />
                <ProviderResultGroup
                  providers={results[SEARCH_ENTITY_TYPE.PROVIDER]}
                  onSelect={entityId => handleSelect(SEARCH_ENTITY_TYPE.PROVIDER, entityId)}
                  getUrl={entityId => getEntityUrl(SEARCH_ENTITY_TYPE.PROVIDER, entityId)}
                  onClose={handleClose}
                />
                <CommandEmpty className="select-none rounded-sm px-2 py-3 text-center text-sm">Aucun résultat</CommandEmpty>
              </>
            ) : null}
          </CommandList>
        </div>
      </div>
    </Command>
  );
};
